import axios from 'axios'
import { billingRefusalFromBody, billingRefusalMessage } from './billingErrors'
import { canViewBilling } from './heldPermissions'
import {
  employmentRevokedFromBody,
  employmentRevokedMessage,
  employmentSuspendedFromBody,
  employmentSuspendedMessage,
  revokedAccountIdOf,
} from './employmentRevoked'
import { useAuthStore } from '@/shared/state/authstore'

const FALLBACK = 'Something went wrong. Try again.'
const FORBIDDEN_MESSAGE = "You don't have permission to do that."
const SERVER_ERROR_MESSAGE = 'Something went wrong on our side. Try again in a minute.'

export class UserFacingError extends Error {
  readonly status?: number

  constructor(message: string, status?: number) {
    super(message)
    this.name = 'UserFacingError'
    this.status = status
  }
}

export function getApiErrorStatus(error: unknown): number | undefined {
  return axios.isAxiosError(error) ? error.response?.status : undefined
}

export function isClientRejection(error: unknown): boolean {
  return (
    error instanceof UserFacingError &&
    error.status !== undefined &&
    error.status >= 400 &&
    error.status < 500
  )
}

export async function getApiErrorMessage(error: unknown): Promise<string> {
  if (!axios.isAxiosError(error)) {
    return error instanceof UserFacingError && error.message ? error.message : FALLBACK
  }
  if (!error.response) {
    return "Couldn't reach the server. Check your connection and try again."
  }
  const status = error.response.status
  if (status >= 500 && status !== 503) {
    return SERVER_ERROR_MESSAGE
  }
  const raw = error.response.data
  const data =
    raw instanceof Blob
      ? await raw
          .text()
          .then((t) => JSON.parse(t))
          .catch(() => null)
      : raw
  // An ended employment is not a missing permission, so it must not fall through to the generic 403 text.
  const revoked = employmentRevokedFromBody(status, data)
  if (revoked || employmentSuspendedFromBody(status, data)) {
    const accountId = revokedAccountIdOf(error)
    const accountName = useAuthStore.getState().availableAccounts.find((a) => a.accountId === accountId)?.accountName
    return revoked ? employmentRevokedMessage(accountName) : employmentSuspendedMessage(accountName)
  }
  // Callers that skip the interceptor's toast land here with the backend's raw text, which tells
  // anyone who reads it to settle the balance. Say it the way the interceptor would for this reader.
  const billingRefusal = billingRefusalFromBody(status, data)
  if (billingRefusal) return billingRefusalMessage(billingRefusal, canViewBilling())
  const fallback = status === 403 ? FORBIDDEN_MESSAGE : FALLBACK
  if (Array.isArray(data)) {
    return data.map((e) => e?.message).filter(Boolean).join(' ') || fallback
  }
  return typeof data?.message === 'string' && data.message ? data.message : fallback
}
